import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  BarChart3, Eye, MousePointer, Target, TrendingUp, DollarSign,
  Pause, Play, Megaphone, Globe, MapPin, Activity, Loader2,
  Upload, Image, Check, X, ExternalLink, CreditCard, Send,
  Info, Download, FileImage, Link2, CheckCircle, Clock, XCircle, Zap
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { uploadFile as uploadFileService } from '../services/uploadService';
import api from '../services/api';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell
} from 'recharts';

const COLORS = ['#ff4500', '#3b82f6', '#22c55e', '#f59e0b', '#8b5cf6', '#ef4444'];

const StatusBadge = ({ status }) => {
  const config = {
    pending: { color: 'bg-amber-50 text-amber-600', icon: Clock, label: 'En attente' },
    approved: { color: 'bg-emerald-50 text-emerald-600', icon: CheckCircle, label: 'Approuve' },
    rejected: { color: 'bg-red-50 text-red-600', icon: XCircle, label: 'Refuse' },
    awaiting_payment: { color: 'bg-blue-50 text-blue-600', icon: CreditCard, label: 'Paiement requis' },
    published: { color: 'bg-brand/10 text-brand', icon: Zap, label: 'Publie' },
    active: { color: 'bg-emerald-50 text-emerald-600', icon: Check, label: 'Active' },
    paused: { color: 'bg-amber-50 text-amber-600', icon: Pause, label: 'En pause' },
  };
  const c = config[status] || config.pending;
  const Icon = c.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-heading font-bold uppercase tracking-wider ${c.color}`}>
      <Icon className="w-3 h-3" /> {c.label}
    </span>
  );
};

const SponsorDashboard = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState('visuals');
  const [loading, setLoading] = useState(true);
  const [formats, setFormats] = useState({});
  const [pricing, setPricing] = useState({});
  const [visuals, setVisuals] = useState([]);
  const [dashData, setDashData] = useState(null);
  const [diffusions, setDiffusions] = useState([]);

  // Upload state
  const [showUpload, setShowUpload] = useState(false);
  const [uploadForm, setUploadForm] = useState({ format_type: 'strip', brand_name: '', banner_text: '', banner_cta: 'En savoir plus', brand_color: '#ff4500', link_url: '', logo_url: '' });
  const [uploading, setUploading] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');

  // Diffusion state
  const [showDiffusion, setShowDiffusion] = useState(false);
  const [selectedVisual, setSelectedVisual] = useState(null);
  const [diffusionForm, setDiffusionForm] = useState({ target_type: 'national', target_value: '', duration_months: 1 });
  const [diffusionSaving, setDiffusionSaving] = useState(false);

  // Format preview popup
  const [previewFormat, setPreviewFormat] = useState(null);

  // Payment confirm after redirect
  useEffect(() => {
    const paymentStatus = searchParams.get('payment');
    const visualId = searchParams.get('visual');
    if (paymentStatus === 'success' && visualId) {
      confirmPayment(visualId);
    }
  }, [searchParams]);

  const confirmPayment = async (visualId) => {
    try {
      await api.post(`/sponsors/visuals/${visualId}/confirm-payment`);
      toast.success('Paiement confirme ! Votre bandeau est maintenant publie.');
      fetchAll();
    } catch {
      toast.error('Erreur de confirmation de paiement');
    }
  };

  const fetchAll = useCallback(async () => {
    try {
      const [fmtRes, visRes, dashRes, diffRes] = await Promise.all([
        api.get('/sponsors/formats'),
        api.get('/sponsors/visuals'),
        api.get('/sponsors/dashboard').catch(() => ({ data: null })),
        api.get('/sponsors/diffusions').catch(() => ({ data: { diffusions: [] } })),
      ]);
      setFormats(fmtRes.data.formats || {});
      setPricing(fmtRes.data.pricing || {});
      setVisuals(visRes.data.visuals || []);
      setDashData(dashRes.data);
      setDiffusions(diffRes.data.diffusions || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Logo upload handler
  const handleLogoSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['png', 'jpg', 'jpeg', 'webp', 'svg'].includes(ext)) {
      toast.error('Format non accepte. Utilisez PNG, JPG, WEBP ou SVG');
      return;
    }
    if (file.size > 200 * 1024) {
      toast.error('Logo trop volumineux. Max: 200 KB');
      return;
    }
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const handleUploadSubmit = async () => {
    if (!uploadForm.brand_name) { toast.error('Renseignez le nom de la marque'); return; }
    if (!uploadForm.banner_text) { toast.error('Renseignez le texte du bandeau'); return; }
    if (!uploadForm.link_url) { toast.error('Renseignez le lien de redirection'); return; }
    setUploading(true);
    try {
      let logoUrl = '';
      // Upload logo if provided
      if (logoFile) {
        const result = await uploadFileService(logoFile, { isImage: true });
        logoUrl = result.url;
      }

      // Create visual
      await api.post('/sponsors/visuals', {
        format_type: uploadForm.format_type,
        brand_name: uploadForm.brand_name,
        banner_text: uploadForm.banner_text,
        banner_cta: uploadForm.banner_cta,
        brand_color: uploadForm.brand_color,
        link_url: uploadForm.link_url,
        logo_url: logoUrl,
      });

      toast.success('Bandeau envoye pour validation !');
      setShowUpload(false);
      setLogoFile(null);
      setLogoPreview('');
      setUploadForm({ format_type: 'strip', brand_name: '', banner_text: '', banner_cta: 'En savoir plus', brand_color: '#ff4500', link_url: '', logo_url: '' });
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setUploading(false);
    }
  };

  // Diffusion handler
  const handleDiffusionSubmit = async () => {
    if (!selectedVisual) return;
    setDiffusionSaving(true);
    try {
      const res = await api.post(`/sponsors/visuals/${selectedVisual.visual_id}/diffusion`, diffusionForm);
      toast.success(`Diffusion configuree: ${res.data.total_price} EUR`);
      setShowDiffusion(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setDiffusionSaving(false);
    }
  };

  // Payment handler
  const handlePay = async (visual) => {
    try {
      const res = await api.post(`/sponsors/visuals/${visual.visual_id}/pay`);
      if (res.data.auto_confirmed) {
        // Free offer or no Square URL — auto-confirmed
        toast.success('Paiement confirme ! Votre bandeau est maintenant publie.');
        fetchAll();
      } else if (res.data.payment_url) {
        // Redirect to Square checkout
        window.location.href = res.data.payment_url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de paiement');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-brand" />
      </div>
    );
  }

  const overview = dashData?.overview || {};

  const tabs = [
    { id: 'visuals', label: 'Mes Visuels', icon: FileImage },
    { id: 'campaigns', label: 'Campagnes en cours', icon: Megaphone },
    { id: 'analytics', label: 'Analytics', icon: TrendingUp },
  ];

  const targetIcons = { test: Check, national: Globe, region: MapPin, sport: Activity, event: Target, premium: Zap };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="sponsor-dashboard">
      {/* Header */}
      <div className="bg-asphalt text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-brand/20"><Megaphone className="w-5 h-5 text-brand" /></div>
                <h1 className="font-heading text-2xl sm:text-3xl font-bold uppercase tracking-wider" data-testid="sponsor-dashboard-title">
                  Sport Ads Network
                </h1>
              </div>
              <p className="text-slate-400 text-sm">{user?.company_name || user?.name} — Dashboard Sponsor</p>
            </div>
            <Button className="btn-primary h-10 gap-2 text-xs" onClick={() => setShowUpload(true)} data-testid="upload-visual-btn">
              <Upload className="w-4 h-4" /> Nouveau visuel
            </Button>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mt-6">
            {tabs.map(tb => (
              <button
                key={tb.id}
                onClick={() => setTab(tb.id)}
                className={`flex items-center gap-2 px-5 py-2.5 text-xs font-heading font-bold uppercase tracking-wider transition-all ${
                  tab === tb.id ? 'bg-brand text-white' : 'bg-white/10 text-white/60 hover:bg-white/20 hover:text-white'
                }`}
                data-testid={`tab-${tb.id}`}
              >
                <tb.icon className="w-4 h-4" /> {tb.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* ========== VISUALS TAB ========== */}
        {tab === 'visuals' && (
          <div className="space-y-8">
            {/* Format Specs */}
            <div className="mb-2">
              <h2 className="font-heading text-xl font-bold uppercase tracking-wider">Communiquez aupres de milliers de sportifs</h2>
              <p className="text-sm text-slate-500 mt-1">Choisissez votre emplacement et touchez votre audience sur l'ensemble du reseau iBookSport.</p>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {Object.entries(formats).map(([key, fmt]) => (
                <div key={key} className="bg-white border border-slate-200 p-6" data-testid={`format-${key}`}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-brand/10"><Image className="w-5 h-5 text-brand" /></div>
                    <div>
                      <h3 className="font-heading font-bold text-sm uppercase">{fmt.name}</h3>
                      <p className="text-xs text-slate-400">{fmt.placement}</p>
                    </div>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between py-2 border-b border-dashed">
                      <span className="text-slate-500">Dimensions</span>
                      <span className="font-heading font-bold">{fmt.width} x {fmt.height} px</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-dashed">
                      <span className="text-slate-500">Formats acceptes</span>
                      <span className="font-heading font-bold">{fmt.formats.join(', ')}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-dashed">
                      <span className="text-slate-500">Taille max</span>
                      <span className="font-heading font-bold">{fmt.max_size_kb} KB</span>
                    </div>
                    <p className="text-xs text-slate-400 pt-2">{fmt.description}</p>
                  </div>
                  {/* Preview link */}
                  <div className="mt-4 pt-3 border-t border-dashed border-slate-200">
                    <button
                      onClick={() => setPreviewFormat(key)}
                      className="inline-flex items-center gap-1.5 text-xs font-heading font-bold uppercase tracking-wider text-brand hover:text-brand/80 transition-colors"
                      data-testid={`preview-format-${key}`}
                    >
                      <Eye className="w-3.5 h-3.5" /> Voir l'apercu
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Pricing Grid */}
            <div className="bg-white border border-slate-200 p-6">
              <h2 className="font-heading text-sm font-bold uppercase tracking-wider text-slate-500 mb-6">Tarifs de diffusion</h2>
              <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                {Object.entries(pricing).map(([key, p]) => {
                  const Icon = targetIcons[key] || Target;
                  return (
                    <div key={key} className={`border p-4 text-center hover:border-brand/30 transition-colors ${key === 'test' ? 'border-emerald-300 bg-emerald-50/30' : 'border-slate-200'}`}>
                      <Icon className="w-6 h-6 mx-auto text-brand mb-2" />
                      <div className="font-heading font-bold text-xs uppercase tracking-wider mb-2">{p.label}</div>
                      <div className="font-heading text-2xl font-bold text-brand">{p.price === 0 ? 'GRATUIT' : `${p.price} EUR`}</div>
                      {p.price > 0 && <div className="text-[10px] text-slate-400 uppercase">/mois</div>}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* My Visuals */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading text-sm font-bold uppercase tracking-wider text-slate-500">Mes visuels ({visuals.length})</h2>
                <Button size="sm" className="btn-primary h-8 gap-1 text-xs" onClick={() => setShowUpload(true)}>
                  <Upload className="w-3 h-3" /> Ajouter un visuel
                </Button>
              </div>

              {visuals.length === 0 ? (
                <div className="bg-white border border-slate-200 p-12 text-center">
                  <FileImage className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                  <h3 className="font-heading font-bold text-lg mb-2">Aucun visuel</h3>
                  <p className="text-slate-500 text-sm mb-6">Telechargez votre premier visuel pour demarrer.</p>
                  <Button className="btn-primary gap-2" onClick={() => setShowUpload(true)}>
                    <Upload className="w-4 h-4" /> Telecharger un visuel
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {visuals.map((v) => (
                    <div key={v.visual_id} className="bg-white border border-slate-200 p-4" data-testid={`visual-${v.visual_id}`}>
                      {/* Contextual preview - shows how ad appears on the real page */}
                      {v.format_type === 'sidebar' ? (
                        /* SIDEBAR: show encart in a mini page context */
                        <div className="mb-4 bg-slate-100 rounded-lg p-3 flex gap-3">
                          {/* Mini main content */}
                          <div className="flex-1 space-y-1.5">
                            <div className="h-16 bg-slate-200 rounded" />
                            <div className="h-1.5 bg-slate-200 rounded w-2/3" />
                            <div className="h-1.5 bg-slate-200 rounded w-full" />
                            <div className="h-1.5 bg-slate-200 rounded w-4/5" />
                            <div className="text-[8px] text-slate-400 font-heading uppercase tracking-widest text-center py-0.5">Page evenement</div>
                          </div>
                          {/* Actual sidebar banner */}
                          <div className="w-[160px] flex-shrink-0 space-y-2">
                            <div className="bg-slate-200 rounded p-1.5"><div className="h-1 bg-slate-300 rounded w-2/3" /></div>
                            <div className="overflow-hidden border border-brand/30"
                              style={{ borderLeft: `3px solid ${v.brand_color || '#ff4500'}`, background: `linear-gradient(135deg, ${v.brand_color || '#ff4500'}0F 0%, #f8fafc 50%, ${v.brand_color || '#ff4500'}08 100%)` }}>
                              <div className="p-2.5">
                                <div className="flex items-center justify-between mb-1.5">
                                  <div className="flex items-center gap-1.5">
                                    {v.logo_url && <img src={v.logo_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${v.logo_url}` : v.logo_url} alt="" className="h-3.5 w-auto max-w-[30px] object-contain" onError={e => { e.target.style.display = 'none'; }} />}
                                    <span className="font-heading font-bold uppercase tracking-[0.1em] text-[7px]" style={{ color: v.brand_color || '#ff4500' }}>{v.brand_name}</span>
                                  </div>
                                  <span className="text-[5px] text-slate-400 font-heading uppercase">AD</span>
                                </div>
                                <p className="text-[9px] text-slate-600 leading-snug mb-1.5 line-clamp-2">{v.banner_text}</p>
                                <span className="text-[7px] font-heading font-bold uppercase tracking-wider" style={{ color: v.brand_color || '#ff4500' }}>{v.banner_cta || 'En savoir plus'}</span>
                              </div>
                            </div>
                            <div className="bg-slate-200 rounded p-1.5"><div className="h-1 bg-slate-300 rounded w-1/2" /></div>
                          </div>
                        </div>
                      ) : (
                        /* STRIP: show bandeau in a mini page context */
                        <div className="mb-4 bg-slate-100 rounded-lg p-3 space-y-2">
                          <div className="flex gap-1.5">
                            {['All','Running','Cycling'].map(s => (
                              <div key={s} className="px-1.5 py-0.5 bg-slate-200 rounded text-[7px] text-slate-400">{s}</div>
                            ))}
                          </div>
                          {/* Actual strip banner */}
                          <div className="border border-brand/30 overflow-hidden" style={{ borderTop: `2px solid ${v.brand_color || '#ff4500'}` }}>
                            <div className="flex items-center justify-between px-3 py-1.5 bg-white/90">
                              <div className="flex items-center gap-2 min-w-0 flex-1">
                                {v.logo_url && <img src={v.logo_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${v.logo_url}` : v.logo_url} alt="" className="h-3.5 w-auto max-w-[30px] object-contain" onError={e => { e.target.style.display = 'none'; }} />}
                                <span className="font-heading font-bold uppercase tracking-[0.1em] text-[7px]" style={{ color: v.brand_color || '#ff4500' }}>{v.brand_name}</span>
                                <span className="text-[5px] text-slate-400 font-heading uppercase border border-slate-300 px-0.5">AD</span>
                                <span className="text-[9px] text-slate-500 truncate">{v.banner_text}</span>
                              </div>
                              <span className="text-[7px] font-heading font-bold uppercase tracking-wider flex-shrink-0" style={{ color: v.brand_color || '#ff4500' }}>{v.banner_cta || 'En savoir plus'}</span>
                            </div>
                          </div>
                          <div className="grid grid-cols-3 gap-1.5">
                            {[1,2,3].map(i => (
                              <div key={i} className="bg-white rounded p-1.5 space-y-1">
                                <div className="h-8 bg-slate-200 rounded" />
                                <div className="h-1 bg-slate-200 rounded w-3/4" />
                              </div>
                            ))}
                          </div>
                          <div className="text-[8px] text-slate-400 font-heading uppercase tracking-widest text-center">Page evenements</div>
                        </div>
                      )}

                      <div className="flex items-center gap-6">
                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-1">
                            <span className="font-heading font-bold text-sm">{v.brand_name}</span>
                            <StatusBadge status={v.status} />
                          </div>
                          <p className="text-xs text-slate-400">{v.format_name}</p>
                          <div className="flex items-center gap-1 mt-1 text-xs text-slate-500">
                            <Link2 className="w-3 h-3" /> {v.link_url}
                          </div>
                          {v.admin_feedback && (
                            <div className={`mt-2 text-xs p-2 ${v.status === 'rejected' ? 'bg-red-50 text-red-600' : 'bg-emerald-50 text-emerald-600'}`}>
                              <Info className="w-3 h-3 inline mr-1" /> {v.admin_feedback}
                            </div>
                          )}
                          {v.status === 'pending' && (
                            <div className="mt-3 p-4 bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-300 rounded-lg">
                              <div className="flex items-center gap-2 mb-2">
                                <div className="w-8 h-8 rounded-full bg-amber-400 flex items-center justify-center animate-pulse">
                                  <Clock className="w-4 h-4 text-white" />
                                </div>
                                <span className="font-heading font-bold uppercase tracking-wider text-amber-800 text-sm">Verification en cours</span>
                              </div>
                              <p className="text-sm text-amber-700 mb-2">L'administrateur verifie votre visuel avant publication. <strong>Merci de patienter (5 min max).</strong></p>
                              <div className="flex items-center gap-2 text-sm font-bold text-brand bg-white/80 p-2.5 rounded border border-brand/20">
                                <Zap className="w-4 h-4 flex-shrink-0" />
                                <span>Une fois approuve → <span className="underline">Choisir diffusion</span> → <span className="underline">Publier</span> pour diffuser votre communication !</span>
                              </div>
                            </div>
                          )}
                          {v.diffusion && (
                            <div className="mt-2 text-xs text-slate-600 bg-slate-50 p-2 flex items-center gap-4">
                              {React.createElement(targetIcons[v.diffusion.target_type] || Target, { className: 'w-3 h-3' })}
                              <span>{pricing[v.diffusion.target_type]?.label} {v.diffusion.target_value ? `— ${v.diffusion.target_value}` : ''} — {v.diffusion.duration_months} mois</span>
                              <span className="font-heading font-bold text-brand">{v.diffusion.total_price === 0 ? 'GRATUIT' : `${v.diffusion.total_price} EUR`}</span>
                            </div>
                          )}
                          {/* Where is my ad? */}
                          {v.status === 'published' && v.diffusion && (
                            <div className="mt-2 flex items-center gap-2 text-xs flex-wrap">
                              <span className="text-emerald-600 font-heading font-bold flex items-center gap-1"><CheckCircle className="w-3 h-3" /> En ligne</span>
                              <span className="text-slate-300">|</span>
                              {v.format_type === 'sidebar' ? (
                                /* Sidebar banners appear on event detail pages */
                                <a href={v.preview_url || '/events'} target="_blank" rel="noopener noreferrer" className="text-brand hover:underline flex items-center gap-1">
                                  <Eye className="w-3 h-3" /> Voir mon encart sur un evenement
                                </a>
                              ) : (
                                /* Strip banners appear on /events and homepage */
                                <>
                                  {v.diffusion.target_type === 'premium' && (
                                    <a href="/" target="_blank" rel="noopener noreferrer" className="text-brand hover:underline flex items-center gap-1">
                                      <Eye className="w-3 h-3" /> Page d'accueil
                                    </a>
                                  )}
                                  {v.diffusion.target_type === 'premium' && <span className="text-slate-300">+</span>}
                                  {v.diffusion.target_type === 'event' && v.diffusion.target_value ? (
                                    <a href={`/events/${v.diffusion.target_value}`} target="_blank" rel="noopener noreferrer" className="text-brand hover:underline flex items-center gap-1">
                                      <Eye className="w-3 h-3" /> Voir mon bandeau sur l'evenement
                                    </a>
                                  ) : (
                                    <a href="/events" target="_blank" rel="noopener noreferrer" className="text-brand hover:underline flex items-center gap-1">
                                      <Eye className="w-3 h-3" /> Voir mon bandeau sur la page evenements
                                    </a>
                                  )}
                                </>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="flex-shrink-0 flex flex-col gap-2">
                          {v.status === 'approved' && (
                            <Button size="sm" className="btn-primary h-8 gap-1 text-xs" onClick={() => { setSelectedVisual(v); setShowDiffusion(true); }} data-testid={`diffuse-${v.visual_id}`}>
                              <Target className="w-3 h-3" /> Choisir diffusion
                            </Button>
                          )}
                          {v.status === 'awaiting_payment' && (
                            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 gap-1 text-xs" onClick={() => handlePay(v)} data-testid={`pay-${v.visual_id}`}>
                              <CreditCard className="w-3 h-3" /> {v.diffusion?.total_price === 0 ? 'Publier (gratuit)' : `Payer ${v.diffusion?.total_price} EUR`}
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ========== CAMPAIGNS TAB ========== */}
        {tab === 'campaigns' && (
          <div className="bg-white border border-slate-200">
            <div className="p-4 border-b bg-slate-50">
              <h2 className="font-heading text-sm font-bold uppercase tracking-wider text-slate-500">Diffusions actives — Toutes les marques</h2>
            </div>
            {diffusions.length === 0 ? (
              <div className="text-center py-16">
                <Megaphone className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <h3 className="font-heading text-lg font-bold mb-2">Aucune diffusion active</h3>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="diffusions-table">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left px-4 py-3 text-[10px] font-heading uppercase tracking-widest text-slate-500">Marque</th>
                      <th className="text-left px-4 py-3 text-[10px] font-heading uppercase tracking-widest text-slate-500">Format</th>
                      <th className="text-left px-4 py-3 text-[10px] font-heading uppercase tracking-widest text-slate-500">Ciblage</th>
                      <th className="text-right px-4 py-3 text-[10px] font-heading uppercase tracking-widest text-slate-500">Impressions</th>
                      <th className="text-right px-4 py-3 text-[10px] font-heading uppercase tracking-widest text-slate-500">Clics</th>
                      <th className="text-right px-4 py-3 text-[10px] font-heading uppercase tracking-widest text-slate-500">CTR</th>
                      <th className="text-right px-4 py-3 text-[10px] font-heading uppercase tracking-widest text-slate-500">Budget</th>
                    </tr>
                  </thead>
                  <tbody>
                    {diffusions.map((d, idx) => {
                      const stats = d.stats || {};
                      const ctr = stats.impressions > 0 ? ((stats.clicks / stats.impressions) * 100).toFixed(2) : '0.00';
                      const TIcon = targetIcons[d.target_type] || Target;
                      return (
                        <tr key={d.campaign_id} className="border-b hover:bg-slate-50 transition-colors" data-testid={`diffusion-row-${idx}`}>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              {d.logo_url && (
                                <img src={d.logo_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${d.logo_url}` : d.logo_url}
                                  alt={d.brand_name} className="h-6 w-auto max-w-[50px] object-contain"
                                  onError={(e) => { e.target.style.display = 'none'; }} />
                              )}
                              <span className="font-heading font-bold text-sm" style={{ color: d.brand_color || '#1e293b' }}>{d.brand_name}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-xs text-slate-500">{d.format_type === 'strip' ? 'Bandeau /events' : d.format_type === 'sidebar' ? 'Encart evenement' : d.format_type || '—'}</td>
                          <td className="px-4 py-3">
                            <span className="inline-flex items-center gap-1 text-xs text-slate-600">
                              <TIcon className="w-3 h-3" /> {d.target_type} {d.target_region || d.target_sport || ''}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right text-sm">{stats.impressions?.toLocaleString()}</td>
                          <td className="px-4 py-3 text-right text-sm font-bold text-brand">{stats.clicks?.toLocaleString()}</td>
                          <td className="px-4 py-3 text-right text-sm">{ctr}%</td>
                          <td className="px-4 py-3 text-right text-sm font-heading font-bold">{d.budget} EUR</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ========== ANALYTICS TAB ========== */}
        {tab === 'analytics' && dashData && (
          <div className="space-y-8">
            {/* KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {[
                { icon: Megaphone, label: 'Campagnes actives', value: overview.active_campaigns, color: '#22c55e' },
                { icon: Eye, label: 'Impressions', value: overview.total_impressions?.toLocaleString(), color: '#3b82f6' },
                { icon: MousePointer, label: 'Clics', value: overview.total_clicks?.toLocaleString(), color: '#ff4500' },
                { icon: Target, label: 'Conversions', value: overview.total_conversions, color: '#8b5cf6' },
                { icon: TrendingUp, label: 'CTR', value: `${overview.ctr}%`, color: '#f59e0b' },
                { icon: DollarSign, label: 'CPC', value: `${overview.cpc} EUR`, color: '#ef4444' },
              ].map((stat, idx) => (
                <div key={idx} className="bg-white border border-slate-200 p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="p-2" style={{ background: `${stat.color}15` }}>
                      <stat.icon className="w-4 h-4" style={{ color: stat.color }} />
                    </div>
                    <span className="text-[10px] font-heading uppercase tracking-wider text-slate-400">{stat.label}</span>
                  </div>
                  <div className="font-heading text-2xl font-bold">{stat.value}</div>
                </div>
              ))}
            </div>

            {/* Charts */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white border border-slate-200 p-6">
                <h2 className="font-heading text-sm font-bold uppercase tracking-wider text-slate-500 mb-4">Performance par ciblage</h2>
                {dashData.by_target && Object.keys(dashData.by_target).length > 0 ? (
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={Object.entries(dashData.by_target).map(([k, v]) => ({ name: k, impressions: v.impressions, clicks: v.clicks }))}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Bar dataKey="impressions" fill="#3b82f6" radius={[2, 2, 0, 0]} />
                      <Bar dataKey="clicks" fill="#ff4500" radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-slate-400 text-center py-10">Pas de donnees</p>
                )}
              </div>
              <div className="bg-white border border-slate-200 p-6">
                <h2 className="font-heading text-sm font-bold uppercase tracking-wider text-slate-500 mb-4">Performance par region</h2>
                {dashData.by_region && Object.keys(dashData.by_region).length > 0 ? (
                  <ResponsiveContainer width="100%" height={260}>
                    <PieChart>
                      <Pie data={Object.entries(dashData.by_region).map(([k, v]) => ({ name: k, value: v.clicks }))}
                        cx="50%" cy="50%" outerRadius={90} dataKey="value"
                        label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}>
                        {Object.keys(dashData.by_region).map((_, idx) => (
                          <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-slate-400 text-center py-10">Pas de donnees</p>
                )}
              </div>
            </div>

            {/* Budget & ROI */}
            <div className="bg-white border border-slate-200 p-6">
              <h2 className="font-heading text-sm font-bold uppercase tracking-wider text-slate-500 mb-4">Budget & ROI</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-slate-50 text-center">
                  <div className="text-xs text-slate-500 mb-1">Budget total</div>
                  <div className="font-heading text-2xl font-bold text-brand">{overview.total_budget?.toLocaleString()} EUR</div>
                </div>
                <div className="p-4 bg-slate-50 text-center">
                  <div className="text-xs text-slate-500 mb-1">CTR moyen</div>
                  <div className="font-heading text-2xl font-bold">{overview.ctr}%</div>
                </div>
                <div className="p-4 bg-slate-50 text-center">
                  <div className="text-xs text-slate-500 mb-1">Cout par clic</div>
                  <div className="font-heading text-2xl font-bold">{overview.cpc} EUR</div>
                </div>
                <div className="p-4 bg-slate-50 text-center">
                  <div className="text-xs text-slate-500 mb-1">Cout / Conversion</div>
                  <div className="font-heading text-2xl font-bold">
                    {overview.total_conversions > 0 ? (overview.total_budget / overview.total_conversions).toFixed(2) : '—'} EUR
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ========== UPLOAD DIALOG ========== */}
      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold uppercase tracking-wider">Creer un bandeau publicitaire</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Live preview - adapts to selected format */}
            <div>
              <label className="text-[10px] font-heading uppercase tracking-widest text-slate-500 mb-1.5 block">Apercu en direct</label>
              {uploadForm.format_type === 'sidebar' ? (
                /* Sidebar/encart preview */
                <div className="max-w-[280px] mx-auto overflow-hidden border border-slate-200"
                  style={{ borderLeft: `3px solid ${uploadForm.brand_color}`, background: `linear-gradient(135deg, ${uploadForm.brand_color}0F 0%, #f8fafc 50%, ${uploadForm.brand_color}08 100%)` }}>
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-2.5">
                      <div className="flex items-center gap-2">
                        {logoPreview && <img src={logoPreview} alt="" className="h-5 w-auto max-w-[40px] object-contain" />}
                        <span className="font-heading font-bold uppercase tracking-[0.15em] text-[10px]" style={{ color: uploadForm.brand_color }}>
                          {uploadForm.brand_name || 'MA MARQUE'}
                        </span>
                      </div>
                      <span className="text-[7px] text-slate-400 font-heading uppercase tracking-widest">AD</span>
                    </div>
                    <p className="text-sm text-slate-600 leading-relaxed mb-3">
                      {uploadForm.banner_text || 'Mon texte publicitaire apparait ici...'}
                    </p>
                    <span className="inline-flex items-center gap-1 text-xs font-heading font-bold uppercase tracking-wider" style={{ color: uploadForm.brand_color }}>
                      {uploadForm.banner_cta || 'EN SAVOIR PLUS'} <ExternalLink className="w-3 h-3" />
                    </span>
                  </div>
                </div>
              ) : (
                /* Strip/bandeau preview */
                <div className="border border-slate-200 overflow-hidden" style={{ borderTop: `2px solid ${uploadForm.brand_color}` }}>
                  <div className="flex items-center justify-between px-4 py-2.5 bg-slate-50/80">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      {logoPreview && <img src={logoPreview} alt="" className="h-5 w-auto max-w-[50px] object-contain" />}
                      <span className="font-heading font-bold uppercase tracking-[0.15em] text-[10px]" style={{ color: uploadForm.brand_color }}>
                        {uploadForm.brand_name || 'MA MARQUE'}
                      </span>
                      <span className="text-[8px] text-slate-400 font-heading uppercase tracking-widest border border-slate-300 px-1 py-0.5">AD</span>
                      <span className="text-xs text-slate-500 truncate">{uploadForm.banner_text || 'Mon texte publicitaire...'}</span>
                    </div>
                    <span className="text-[10px] font-heading font-bold uppercase tracking-wider whitespace-nowrap" style={{ color: uploadForm.brand_color }}>
                      {uploadForm.banner_cta || 'EN SAVOIR PLUS'}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Format selector */}
            <div>
              <label className="text-[10px] font-heading uppercase tracking-widest text-slate-500 mb-1.5 block">Emplacement *</label>
              <div className="grid grid-cols-2 gap-3">
                {Object.entries(formats).map(([key, fmt]) => (
                  <button key={key} onClick={() => setUploadForm(p => ({ ...p, format_type: key }))}
                    className={`p-3 border text-left transition-all ${uploadForm.format_type === key ? 'border-brand bg-brand/5' : 'border-slate-200 hover:border-slate-300'}`}
                    data-testid={`format-select-${key}`}>
                    <div className="font-heading font-bold text-xs uppercase">{fmt.name}</div>
                    <div className="text-[10px] text-slate-400 mt-1">{fmt.placement}</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Brand name */}
              <div>
                <label className="text-[10px] font-heading uppercase tracking-widest text-slate-500 mb-1.5 block">Nom de la marque *</label>
                <input type="text" value={uploadForm.brand_name} onChange={e => setUploadForm(p => ({ ...p, brand_name: e.target.value }))}
                  placeholder="NIKE, DECATHLON..." className="w-full h-10 px-3 border border-slate-200 text-sm outline-none focus:border-brand uppercase"
                  data-testid="upload-brand-name" />
              </div>
              {/* Brand color */}
              <div>
                <label className="text-[10px] font-heading uppercase tracking-widest text-slate-500 mb-1.5 block">Couleur de marque</label>
                <div className="flex items-center gap-2">
                  <input type="color" value={uploadForm.brand_color} onChange={e => setUploadForm(p => ({ ...p, brand_color: e.target.value }))}
                    className="w-10 h-10 border border-slate-200 cursor-pointer" data-testid="upload-brand-color" />
                  <input type="text" value={uploadForm.brand_color} onChange={e => setUploadForm(p => ({ ...p, brand_color: e.target.value }))}
                    className="w-full h-10 px-3 border border-slate-200 text-sm outline-none focus:border-brand font-mono" />
                </div>
              </div>
            </div>

            {/* Banner text */}
            <div>
              <label className="text-[10px] font-heading uppercase tracking-widest text-slate-500 mb-1.5 block">Texte du bandeau * <span className="text-slate-300">(max 80 caracteres)</span></label>
              <input type="text" maxLength={80} value={uploadForm.banner_text} onChange={e => setUploadForm(p => ({ ...p, banner_text: e.target.value }))}
                placeholder="Equipez-vous pour la performance." className="w-full h-10 px-3 border border-slate-200 text-sm outline-none focus:border-brand"
                data-testid="upload-banner-text" />
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* CTA text */}
              <div>
                <label className="text-[10px] font-heading uppercase tracking-widest text-slate-500 mb-1.5 block">Texte du bouton</label>
                <input type="text" maxLength={20} value={uploadForm.banner_cta} onChange={e => setUploadForm(p => ({ ...p, banner_cta: e.target.value }))}
                  placeholder="En savoir plus" className="w-full h-10 px-3 border border-slate-200 text-sm outline-none focus:border-brand"
                  data-testid="upload-banner-cta" />
              </div>
              {/* Logo upload */}
              <div>
                <label className="text-[10px] font-heading uppercase tracking-widest text-slate-500 mb-1.5 block">Logo <span className="text-slate-300">(optionnel, max 200KB)</span></label>
                <div className="border border-dashed border-slate-200 h-10 flex items-center justify-center cursor-pointer hover:border-brand/30"
                  onClick={() => document.getElementById('logo-file-input').click()}>
                  {logoPreview ? (
                    <img src={logoPreview} alt="" className="h-6 w-auto object-contain" />
                  ) : (
                    <span className="text-xs text-slate-400">Cliquer pour ajouter</span>
                  )}
                </div>
                <input id="logo-file-input" type="file" accept=".png,.jpg,.jpeg,.webp,.svg" className="hidden" onChange={handleLogoSelect} />
              </div>
            </div>

            {/* Link URL */}
            <div>
              <label className="text-[10px] font-heading uppercase tracking-widest text-slate-500 mb-1.5 block">Lien de redirection *</label>
              <div className="flex items-center gap-2">
                <ExternalLink className="w-4 h-4 text-slate-400 flex-shrink-0" />
                <input type="url" value={uploadForm.link_url} onChange={e => setUploadForm(p => ({ ...p, link_url: e.target.value }))}
                  placeholder="https://www.mamarque.com/promo" className="w-full h-10 px-3 border border-slate-200 text-sm outline-none focus:border-brand"
                  data-testid="upload-link-url" />
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => setShowUpload(false)}>Annuler</Button>
              <Button className="btn-primary flex-1 gap-2" onClick={handleUploadSubmit} disabled={uploading} data-testid="submit-visual-btn">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Envoyer pour validation
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ========== DIFFUSION DIALOG ========== */}
      <Dialog open={showDiffusion} onOpenChange={setShowDiffusion}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold uppercase tracking-wider">Choisir la diffusion</DialogTitle>
          </DialogHeader>
          {selectedVisual && (
            <div className="space-y-4 overflow-hidden">
              {/* Preview adapts to format_type */}
              {selectedVisual.format_type === 'sidebar' ? (
                <div className="max-w-[260px] overflow-hidden border border-slate-200"
                  style={{ borderLeft: `3px solid ${selectedVisual.brand_color || '#ff4500'}`, background: `linear-gradient(135deg, ${selectedVisual.brand_color || '#ff4500'}0F 0%, #f8fafc 50%, ${selectedVisual.brand_color || '#ff4500'}08 100%)` }}>
                  <div className="p-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {selectedVisual.logo_url && <img src={selectedVisual.logo_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${selectedVisual.logo_url}` : selectedVisual.logo_url} alt="" className="h-4 w-auto max-w-[35px] object-contain" onError={e => { e.target.style.display = 'none'; }} />}
                        <span className="font-heading font-bold uppercase tracking-[0.1em] text-[9px]" style={{ color: selectedVisual.brand_color || '#ff4500' }}>{selectedVisual.brand_name}</span>
                      </div>
                      <span className="text-[7px] text-slate-400 font-heading uppercase">AD</span>
                    </div>
                    <p className="text-xs text-slate-600 leading-relaxed mb-2">{selectedVisual.banner_text}</p>
                    <span className="text-[9px] font-heading font-bold uppercase tracking-wider" style={{ color: selectedVisual.brand_color || '#ff4500' }}>{selectedVisual.banner_cta || 'En savoir plus'}</span>
                  </div>
                </div>
              ) : (
                <div className="border border-slate-200 overflow-hidden" style={{ borderTop: `2px solid ${selectedVisual.brand_color || '#ff4500'}` }}>
                  <div className="flex items-center justify-between px-3 py-2 bg-slate-50/80 gap-2">
                    <div className="flex items-center gap-2 min-w-0 flex-1 overflow-hidden">
                      {selectedVisual.logo_url && <img src={selectedVisual.logo_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${selectedVisual.logo_url}` : selectedVisual.logo_url} alt="" className="h-4 w-auto max-w-[40px] object-contain flex-shrink-0" onError={e => { e.target.style.display = 'none'; }} />}
                      <span className="font-heading font-bold uppercase tracking-[0.1em] text-[9px] flex-shrink-0" style={{ color: selectedVisual.brand_color || '#ff4500' }}>{selectedVisual.brand_name}</span>
                      <span className="text-[7px] text-slate-400 font-heading uppercase border border-slate-300 px-1 py-0.5 flex-shrink-0">AD</span>
                      <span className="text-[11px] text-slate-500 truncate">{selectedVisual.banner_text}</span>
                    </div>
                    <span className="text-[9px] font-heading font-bold uppercase tracking-wider flex-shrink-0" style={{ color: selectedVisual.brand_color || '#ff4500' }}>{selectedVisual.banner_cta || 'En savoir plus'}</span>
                  </div>
                </div>
              )}

              {/* Target type */}
              <div>
                <label className="text-[10px] font-heading uppercase tracking-widest text-slate-500 mb-2 block">Type de ciblage</label>
                <div className="space-y-2">
                  {Object.entries(pricing).map(([key, p]) => {
                    const Icon = targetIcons[key] || Target;
                    return (
                      <button
                        key={key}
                        onClick={() => setDiffusionForm(f => ({ ...f, target_type: key }))}
                        className={`w-full flex items-center justify-between p-3 border transition-all ${
                          diffusionForm.target_type === key ? 'border-brand bg-brand/5' : 'border-slate-200 hover:border-slate-300'
                        }`}
                        data-testid={`target-${key}`}
                      >
                        <span className="flex items-center gap-2 text-sm">
                          <Icon className="w-4 h-4 text-brand" /> {p.label}
                        </span>
                        <span className="font-heading font-bold text-brand text-sm whitespace-nowrap">{p.price === 0 ? 'GRATUIT' : `${p.price} EUR/mois`}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Target value for region/sport/event */}
              {['region', 'sport', 'event'].includes(diffusionForm.target_type) && (
                <div>
                  <label className="text-[10px] font-heading uppercase tracking-widest text-slate-500 mb-1.5 block">
                    {diffusionForm.target_type === 'region' ? 'Region' : diffusionForm.target_type === 'sport' ? 'Sport' : 'ID Evenement'}
                  </label>
                  <input
                    type="text"
                    value={diffusionForm.target_value}
                    onChange={e => setDiffusionForm(f => ({ ...f, target_value: e.target.value }))}
                    placeholder={diffusionForm.target_type === 'region' ? 'Ile-de-France' : diffusionForm.target_type === 'sport' ? 'running' : 'evt_XXXXXXXX'}
                    className="w-full h-10 px-3 border border-slate-200 text-sm outline-none focus:border-brand"
                    data-testid="target-value-input"
                  />
                </div>
              )}

              {/* Duration */}
              <div>
                <label className="text-[10px] font-heading uppercase tracking-widest text-slate-500 mb-1.5 block">Duree (mois)</label>
                <div className="flex gap-2">
                  {[1, 3, 6, 12].map(m => (
                    <button
                      key={m}
                      onClick={() => setDiffusionForm(f => ({ ...f, duration_months: m }))}
                      className={`flex-1 py-2 text-sm font-heading font-bold border transition-all ${
                        diffusionForm.duration_months === m ? 'border-brand bg-brand text-white' : 'border-slate-200 hover:border-slate-300'
                      }`}
                      data-testid={`duration-${m}`}
                    >
                      {m}
                    </button>
                  ))}
                </div>
              </div>

              {/* Total */}
              <div className="bg-brand/5 border border-brand/20 p-4 text-center">
                <div className="text-xs text-slate-500 mb-1">Total a payer</div>
                <div className="font-heading text-3xl font-bold text-brand">
                  {(pricing[diffusionForm.target_type]?.price || 0) * diffusionForm.duration_months === 0
                    ? 'GRATUIT'
                    : `${(pricing[diffusionForm.target_type]?.price || 0) * diffusionForm.duration_months} EUR`}
                </div>
                {(pricing[diffusionForm.target_type]?.price || 0) > 0 && (
                  <div className="text-xs text-slate-400">{diffusionForm.duration_months} mois x {pricing[diffusionForm.target_type]?.price} EUR</div>
                )}
              </div>

              <div className="flex gap-3">
                <Button variant="outline" className="flex-1" onClick={() => setShowDiffusion(false)}>Annuler</Button>
                <Button className="btn-primary flex-1 gap-2" onClick={handleDiffusionSubmit} disabled={diffusionSaving} data-testid="submit-diffusion-btn">
                  {diffusionSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
                  Valider et payer
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ========== FORMAT PREVIEW DIALOG ========== */}
      <Dialog open={!!previewFormat} onOpenChange={() => setPreviewFormat(null)}>
        <DialogContent className={previewFormat === 'strip' ? 'max-w-3xl max-h-[90vh] overflow-y-auto' : 'max-w-lg max-h-[90vh] overflow-y-auto'}>
          <DialogHeader>
            <DialogTitle className="font-heading font-bold uppercase tracking-wider text-sm">
              Apercu — {previewFormat === 'strip' ? 'Bandeau Evenements & Accueil' : 'Encart Page Evenement'}
            </DialogTitle>
          </DialogHeader>

          {previewFormat === 'strip' ? (
            <div className="space-y-5">
              <p className="text-xs text-slate-500">Voici comment les bandeaux apparaissent sur les pages evenements et la page d'accueil :</p>

              {/* Example 1: NIKE */}
              <div>
                <div className="text-[9px] font-heading uppercase tracking-widest text-slate-400 mb-1.5">Exemple 1</div>
                <div className="border border-slate-200 overflow-hidden" style={{ borderTop: '2px solid #111111' }}>
                  <div className="flex items-center justify-between px-5 py-3 bg-slate-50/80">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/a/a6/Logo_NIKE.svg/200px-Logo_NIKE.svg.png"
                        alt="Nike" className="h-5 w-auto max-w-[40px] object-contain" />
                      <span className="font-heading font-bold uppercase tracking-[0.15em] text-[10px] whitespace-nowrap" style={{ color: '#111111' }}>NIKE</span>
                      <span className="text-[8px] text-slate-400 font-heading uppercase tracking-widest border border-slate-300 px-1.5 py-0.5">SPONSORISE</span>
                      <div className="hidden sm:block w-px h-5 bg-slate-300/30" />
                      <span className="text-sm text-slate-600 truncate">Equipez-vous pour la performance. Nouvelle collection Running 2026.</span>
                    </div>
                    <span className="text-xs font-heading font-bold uppercase tracking-wider whitespace-nowrap ml-3 flex items-center gap-1" style={{ color: '#111111' }}>
                      DECOUVRIR <ExternalLink className="w-3 h-3" />
                    </span>
                  </div>
                </div>
              </div>

              {/* Example 2: DECATHLON */}
              <div>
                <div className="text-[9px] font-heading uppercase tracking-widest text-slate-400 mb-1.5">Exemple 2</div>
                <div className="border border-slate-200 overflow-hidden" style={{ borderTop: '2px solid #0066CC' }}>
                  <div className="flex items-center justify-between px-5 py-3 bg-slate-50/80">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/0/08/Decathlon_Logo.png/200px-Decathlon_Logo.png"
                        alt="Decathlon" className="h-5 w-auto max-w-[60px] object-contain" />
                      <span className="font-heading font-bold uppercase tracking-[0.15em] text-[10px] whitespace-nowrap" style={{ color: '#0066CC' }}>DECATHLON</span>
                      <span className="text-[8px] text-slate-400 font-heading uppercase tracking-widest border border-slate-300 px-1.5 py-0.5">SPONSORISE</span>
                      <div className="hidden sm:block w-px h-5 bg-slate-300/30" />
                      <span className="text-sm text-slate-600 truncate">Le sport pour tous. -20% sur la gamme trail avec le code IBOOKSPORT.</span>
                    </div>
                    <span className="text-xs font-heading font-bold uppercase tracking-wider whitespace-nowrap ml-3 flex items-center gap-1" style={{ color: '#0066CC' }}>
                      PROFITER <ExternalLink className="w-3 h-3" />
                    </span>
                  </div>
                </div>
              </div>

              {/* Context: where it shows */}
              <div className="bg-slate-100 rounded-lg p-4">
                <div className="text-[9px] text-slate-400 font-heading uppercase tracking-widest text-center mb-3">Emplacement sur la page</div>
                <div className="space-y-2">
                  <div className="h-8 bg-slate-200 rounded" />
                  <div className="h-3 bg-slate-200 rounded w-1/4" />
                  <div className="flex gap-1.5">
                    {['All','Running','Cycling','Trail'].map(s => (
                      <div key={s} className="px-2 py-1 bg-slate-200 rounded text-[8px] text-slate-400">{s}</div>
                    ))}
                  </div>
                  {/* The banner position */}
                  <div className="border-2 border-brand/40 border-dashed rounded p-1.5 bg-brand/5">
                    <div className="flex items-center gap-2 px-2 py-1">
                      <div className="w-3 h-3 rounded-full bg-brand/30" />
                      <div className="h-1.5 bg-brand/30 rounded w-16" />
                      <div className="h-1.5 bg-slate-300 rounded flex-1" />
                      <div className="h-1.5 bg-brand/30 rounded w-12" />
                    </div>
                    <div className="text-[8px] text-brand font-heading uppercase text-center mt-0.5">Votre bandeau ici</div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 mt-2">
                    {[1,2,3].map(i => (
                      <div key={i} className="bg-white rounded p-2 space-y-1.5">
                        <div className="h-10 bg-slate-200 rounded" />
                        <div className="h-1.5 bg-slate-200 rounded w-3/4" />
                        <div className="h-1.5 bg-slate-200 rounded w-1/2" />
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 text-[10px] text-slate-400">
                <Info className="w-3 h-3 flex-shrink-0" />
                <span>Le bandeau s'affiche entre les filtres et la liste des evenements, sur toute la largeur. Il est aussi visible sur la page d'accueil.</span>
              </div>
            </div>
          ) : (
            <div className="space-y-5">
              <p className="text-xs text-slate-500">Voici comment les encarts apparaissent dans la sidebar d'un evenement :</p>

              {/* Context mockup with real sidebar example */}
              <div className="flex gap-4">
                {/* Main content mock */}
                <div className="flex-1 bg-slate-100 rounded-lg p-3 space-y-2">
                  <div className="h-28 bg-slate-200 rounded" />
                  <div className="h-2.5 bg-slate-200 rounded w-2/3" />
                  <div className="h-2 bg-slate-200 rounded w-full" />
                  <div className="h-2 bg-slate-200 rounded w-5/6" />
                  <div className="text-[9px] text-slate-400 font-heading uppercase tracking-widest text-center py-1">Detail de l'evenement</div>
                  <div className="h-2 bg-slate-200 rounded w-full" />
                  <div className="h-2 bg-slate-200 rounded w-4/5" />
                  <div className="h-2 bg-slate-200 rounded w-3/4" />
                  <div className="h-8 bg-brand/20 rounded flex items-center justify-center">
                    <span className="text-[8px] text-brand font-heading uppercase">S'inscrire</span>
                  </div>
                </div>

                {/* Sidebar with real banner example */}
                <div className="w-[200px] flex-shrink-0 space-y-3">
                  <div className="bg-slate-100 rounded p-2 space-y-1">
                    <div className="h-1.5 bg-slate-200 rounded w-2/3" />
                    <div className="h-1.5 bg-slate-200 rounded w-full" />
                    <div className="h-1.5 bg-slate-200 rounded w-1/2" />
                  </div>

                  {/* Real example: SALOMON sidebar banner */}
                  <div className="border-2 border-brand/40 border-dashed rounded overflow-hidden">
                    <div className="overflow-hidden"
                      style={{ borderLeft: '3px solid #E4002B', background: 'linear-gradient(135deg, rgba(228,0,43,0.06) 0%, #f8fafc 50%, rgba(228,0,43,0.03) 100%)' }}>
                      <div className="p-3">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-1.5">
                            <span className="font-heading font-bold uppercase tracking-[0.15em] text-[9px]" style={{ color: '#E4002B' }}>SALOMON</span>
                          </div>
                          <span className="text-[6px] text-slate-400 font-heading uppercase tracking-widest">AD</span>
                        </div>
                        <p className="text-[10px] text-slate-600 leading-relaxed mb-2.5">Ne faites qu'un avec le terrain. Gamme Trail Running 2026.</p>
                        <span className="inline-flex items-center gap-1 text-[9px] font-heading font-bold uppercase tracking-wider" style={{ color: '#E4002B' }}>
                          DECOUVRIR <ExternalLink className="w-2.5 h-2.5" />
                        </span>
                      </div>
                    </div>
                    <div className="text-[8px] text-brand font-heading uppercase text-center py-1 bg-brand/5">Votre encart ici</div>
                  </div>

                  <div className="bg-slate-100 rounded p-2 space-y-1">
                    <div className="h-1.5 bg-slate-200 rounded w-1/2" />
                    <div className="h-1.5 bg-slate-200 rounded w-3/4" />
                  </div>
                </div>
              </div>

              {/* Standalone example */}
              <div>
                <div className="text-[9px] font-heading uppercase tracking-widest text-slate-400 mb-1.5">Autre exemple (GARMIN)</div>
                <div className="max-w-[260px] mx-auto overflow-hidden"
                  style={{ borderLeft: '3px solid #007DC3', background: 'linear-gradient(135deg, rgba(0,125,195,0.06) 0%, #f8fafc 50%, rgba(0,125,195,0.03) 100%)' }}>
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-1.5">
                        <span className="font-heading font-bold uppercase tracking-[0.15em] text-[10px]" style={{ color: '#007DC3' }}>GARMIN</span>
                      </div>
                      <span className="text-[7px] text-slate-400 font-heading uppercase tracking-widest">AD</span>
                    </div>
                    <p className="text-sm text-slate-600 leading-relaxed mb-3">Forerunner 965 — La montre GPS qui vous accompagne sur tous les terrains.</p>
                    <span className="inline-flex items-center gap-1.5 text-xs font-heading font-bold uppercase tracking-wider" style={{ color: '#007DC3' }}>
                      COMMANDER <ExternalLink className="w-3 h-3" />
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 text-[10px] text-slate-400">
                <Info className="w-3 h-3 flex-shrink-0" />
                <span>L'encart s'affiche dans la colonne laterale de la page evenement cible, entre les informations pratiques.</span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SponsorDashboard;
